import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { CellValueChangedEvent, GridApi, IRowNode } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { cloneDeep } from 'lodash';
import { Dialog } from 'primereact/dialog';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { addToast } from '@/app/slices/toast';
import {
  MassInterimEmbryosInput,
  StageGradeCountInput,
  TOA_GetFertilizationsEmbryos,
  TOA_GetFertilizationsForFreezing,
  TOA_GetFertilizationsIntEmbryos,
} from '@/rtk-query.gql';

import {
  useAddIntEmbryos,
  useDeactivateInterimEmbryo,
  useLazyFertAndEmbryosFreezeQuery,
  useUpdateEmbryoTransaction,
  useUpdateInterimEmbryoTransaction,
} from '../../../services/freeze/hooks';
import { useFreezeContext } from '../hooks/useFreezeContext';
import { FIELD_NAMES, SLAUGHTER_HOUSE_SITES } from '../utils/constants';
import { SelectionService } from '../utils/selectionService';
import { LiveEmbryoGrid } from './LiveEmbryoGrid';
import { TempEmbryoGrid } from './TempEmbryoGrid';

type FreezeEmbryoGridProps = {
  rowData?: TOA_GetFertilizationsForFreezing;
  onEdit: (id: string) => void;
  onLiveEmbryoSelectionChanged: (fertId: string) => void;
  onTempEmbryoSelectionChanged: (fertId: string) => void;
  isExport: boolean;
  index: number;
  refetchFertEmbryoData: () => void;
  gridApi: GridApi<TOA_GetFertilizationsForFreezing>;
  fertEmbryosData: { fertId: string; liveEmbryos: any[]; tempEmbryos: any[] }[];
  fertGridRef: React.MutableRefObject<AgGridReact>;
  node: IRowNode;
  api: GridApi;
  fertilizationIds: string[] | undefined;
};

const MemoizedLiveEmbryoGrid = memo(LiveEmbryoGrid);
const MemoizedTempEmbryoGrid = memo(TempEmbryoGrid);

export const FreezeEmbryoGrid = memo(
  ({
    rowData,
    onEdit,
    onLiveEmbryoSelectionChanged,
    onTempEmbryoSelectionChanged,
    isExport,
    refetchFertEmbryoData,
    gridApi,
    fertEmbryosData,
    node,
    api,
    fertilizationIds,
  }: FreezeEmbryoGridProps) => {
    const rowId = node.id!;
    const dispatch = useDispatch();
    const { mode } = useAppSelector((state) => state.preferences);
    const {
      form: {
        storageLocation,
        freezeDay,
        grader,
        freezeTech,
        freezer,
        embryosPerStraw,
        embryosPerVitStraw,
      },
      allTempEmbryosSelected,
      isUpdateRunning,
      setIsUpdateRunning,
      registerGridRefs,
      unregisterGridRefs,
      isGridsExpanded,
    } = useFreezeContext();

    const tempEmbryosGridRef = useRef<AgGridReact>(null);
    const liveEmbryosGridRef = useRef<AgGridReact>(null);

    useEffect(() => {
      if (rowData?.fertId) {
        registerGridRefs(
          rowData.fertId,
          liveEmbryosGridRef,
          tempEmbryosGridRef,
        );
      }
      return () => {
        if (rowData?.fertId) {
          unregisterGridRefs(rowData.fertId);
        }
      };
    }, [rowData?.fertId, registerGridRefs, unregisterGridRefs]);

    useEffect(() => {
      return () => {
        if (!api.isDestroyed()) {
          api.removeDetailGridInfo(rowId);
        }
      };
    }, [api, rowId]);

    const [params, setParams] = useSearchParams();
    const fertilizationDate = params.get('date');
    const fertilizationSiteId = params.get('sId');
    const tempAdded = params.get('temp');

    const [showSubmitErrorDialog, setShowSubmitErrorDialog] = useState(false);
    const [tempEmbryoLoading, setTempEmbryoLoading] = useState(false);

    const isSlaughterHouse = SLAUGHTER_HOUSE_SITES.includes(
      rowData?.opuSite as string,
    );
    const { mutateAsync: updateEmbryo } = useUpdateEmbryoTransaction();
    const { mutateAsync: updateIntEmbryo } =
      useUpdateInterimEmbryoTransaction();
    const { mutateAsync: addIntEmbryos } = useAddIntEmbryos();
    const { mutateAsync: deactivateIntEmbryo } = useDeactivateInterimEmbryo();
    const getFertAndEmbryos = useLazyFertAndEmbryosFreezeQuery();

    const validateStageAndGrade = useCallback(
      (value: number, field: string) => {
        if (field === 'stage' && (value < 3 || value > 9)) {
          dispatch(
            addToast({
              severity: 'error',
              summary: 'Invalid Stage',
              detail: 'Stage must be between 3 and 9',
              sticky: false,
              closable: true,
            }),
          );
          return false;
        } else if (field === 'grade' && (value < 1 || value > 3)) {
          dispatch(
            addToast({
              severity: 'error',
              summary: 'Invalid Grade',
              detail: 'Grade must be between 1 and 3',
              sticky: false,
              closable: true,
            }),
          );
          return false;
        }

        return true;
      },
      [dispatch],
    );

    const handleUpdateEmbryo = (params: CellValueChangedEvent) => {
      try {
        setIsUpdateRunning(true);
        // Defer selection to avoid disrupting AG Grid's tab navigation
        setTimeout(() => params.node.setSelected(true), 0);
        let field = params.column.getColDef().field as string;
        const originalField = field;
        let value = params.data[field];

        if (field === FIELD_NAMES.GRADER_7_NAME) {
          field = FIELD_NAMES.GRADER_7_ID;
        }
        if (field === FIELD_NAMES.FREEZE_TECH_NAME) {
          field = FIELD_NAMES.FREEZE_TECH_ID;
        }
        if (field === FIELD_NAMES.GRADE) {
          if (parseInt(value) === 3) {
            params.node.setDataValue('lowQuality', true);
          }
        }
        if (field === FIELD_NAMES.D65) {
          value = value === true ? 6.5 : null;
          field = FIELD_NAMES.FREEZE_TIME;
        }

        if (field === FIELD_NAMES.D7) {
          value = value === true ? 7 : null;
          field = FIELD_NAMES.FREEZE_TIME;
        }
        if (field === FIELD_NAMES.STAGE) {
          if (parseInt(value) >= 8) {
            params.node.setDataValue('crackedZona', true);
          }
        }
        updateEmbryo({
          id: params.data.id,
          field,
          value: value.toString(),
        }).then(() => {
          if (
            originalField === FIELD_NAMES.D65 ||
            originalField === FIELD_NAMES.D7
          ) {
            refetchFertEmbryoData();
          }
        });
      } catch (error) {
        console.error(error);
      } finally {
        setIsUpdateRunning(false);
      }
    };

    const handleUpdateTempEmbryo = useCallback(
      (params: CellValueChangedEvent) => {
        try {
          // Defer selection to avoid disrupting AG Grid's tab navigation
          setTimeout(() => params.node.setSelected(true), 0);
          let field = params.column.getColDef().field as string;
          const originalField = field;
          let value = params.data[field];

          if (field === FIELD_NAMES.GRADE && value) {
            if (!freezeDay || !grader) {
              dispatch(
                addToast({
                  severity: 'error',
                  summary: 'Invalid Stage',
                  detail: 'Freeze Day and Grader must be selected',
                  sticky: false,
                  closable: true,
                }),
              );
              params.node.setDataValue(field, undefined);
              return;
            }
            const isValid = validateStageAndGrade(
              parseInt(value),
              FIELD_NAMES.GRADE,
            );
            if (parseInt(value) === 3) {
              params.node.setDataValue('lowQuality', true);
            }
            if (!isValid) {
              params.node.setDataValue(field, params.oldValue);
              return;
            }
          }

          if (field === FIELD_NAMES.STAGE && value) {
            if (!freezeDay || !grader) {
              dispatch(
                addToast({
                  severity: 'error',
                  summary: 'Invalid Stage',
                  detail: 'Freeze Day and Grader must be selected',
                  sticky: false,
                  closable: true,
                }),
              );
              params.node.setDataValue(field, undefined);
              return;
            }
            const isValid = validateStageAndGrade(
              parseInt(value),
              FIELD_NAMES.STAGE,
            );
            if (!isValid) {
              params.node.setDataValue(field, params.oldValue);
              return;
            }
            if (parseInt(value) >= 8) {
              params.node.setDataValue('crackedZona', true);
            }
          }

          if (field === FIELD_NAMES.D65) {
            value = value === true ? 6.5 : null;
            field = FIELD_NAMES.FREEZE_TIME;
          }

          if (field === FIELD_NAMES.D7) {
            value = value === true ? 7 : null;
            field = FIELD_NAMES.FREEZE_TIME;
          }

          if (field === FIELD_NAMES.DC) {
            value = value === 1 ? true : false;
          }

          setIsUpdateRunning(true);
          updateIntEmbryo({
            id: params.data.embryoFreezeEntryId,
            field,
            value: value ? value.toString() : null,
          }).then(() => {
            if (
              originalField === FIELD_NAMES.D65 ||
              originalField === FIELD_NAMES.D7
            ) {
              refetchFertEmbryoData();
            }
          });
        } catch (error) {
          console.error(error);
        } finally {
          setIsUpdateRunning(false);
        }
      },
      [
        setIsUpdateRunning,
        updateIntEmbryo,
        freezeDay,
        grader,
        validateStageAndGrade,
        dispatch,
        refetchFertEmbryoData,
      ],
    );

    const handleDeactivateTempEmbryo = async () => {
      try {
        setIsUpdateRunning(true);
        const { tempEmbryoIds } = SelectionService.getSelection();

        const currentFertEmbryos =
          fertEmbryosData?.find((fert) => fert.fertId === rowData?.fertId)
            ?.tempEmbryos || [];

        const embryosToDeactivate = currentFertEmbryos
          .filter((embryo) =>
            tempEmbryoIds.includes(embryo.embryoFreezeEntryId as string),
          )
          .map((embryo) => embryo.embryoFreezeEntryId);

        if (embryosToDeactivate.length > 0) {
          const response = await deactivateIntEmbryo({
            items: embryosToDeactivate,
          });

          if (response?.deactivateInterimEmbryo === false) {
            dispatch(
              addToast({
                severity: 'error',
                summary: 'Server Error',
                detail: 'Could not deactivate int emb',
                life: 3000,
              }),
            );
          }
          if (response?.deactivateInterimEmbryo === true) {
            dispatch(
              addToast({
                severity: 'success',
                summary: 'Success',
                detail: 'Int Emb Deactivated',
                life: 3000,
              }),
            );
            refetchFertEmbryoData();
            SelectionService.updateSelection('temp', []);
          }

          if (fertEmbryosData) {
            let fertEmbryos = [];
            if (tempAdded === 'true') {
              fertEmbryos = await getFertAndEmbryos({
                fertilizationDate,
                fertilizationSiteId,
                fertilizationIds,
              });
            } else {
              fertEmbryos = fertEmbryosData;
            }

            const remainingTempEmbryoIds = tempEmbryoIds.filter(
              (id) => !embryosToDeactivate.includes(id),
            );
            SelectionService.updateSelection('temp', remainingTempEmbryoIds);

            fertEmbryosData = fertEmbryos.map((fert) => {
              if (fert.fertId === rowData?.fertId) {
                return {
                  ...fert,
                  tempEmbryos: (fert.tempEmbryos || []).filter(
                    (embryo) =>
                      !embryosToDeactivate.includes(embryo.embryoFreezeEntryId),
                  ),
                };
              }
              return fert;
            });
          }

          if (tempEmbryosGridRef.current?.api) {
            const api = tempEmbryosGridRef.current.api;
            const updatedTempEmbryos =
              fertEmbryosData.find((fert) => fert.fertId === rowData?.fertId)
                ?.tempEmbryos || [];
            api.setGridOption('rowData', updatedTempEmbryos);
          }
        }
      } finally {
        setIsUpdateRunning(false);
      }
    };

    const handleAddTempEmbryos = async (
      count: number,
      stageGradeCounts?: StageGradeCountInput[],
    ) => {
      setTempEmbryoLoading(true);
      setIsUpdateRunning(true);

      const tempEmbryoInput: MassInterimEmbryosInput = {
        fertID: rowData?.fertId as string,
        cane: rowData?.cane,
        g1Count: count,
        g2Count: 0,
        freezeDay: freezeDay?.toString() as string,
        storageLoc: storageLocation as string,
        graderID: grader as string,
        freezeTechID: freezeTech as string,
        freezer: freezer as string,
        g1CountToCreate: count,
        g2CountToCreate: 0,
        embsPerVitStraw: embryosPerVitStraw as number,
        embsPerStraw: embryosPerStraw as number,
        grade1: null,
        grade2: null,
        slaughterHouse: isSlaughterHouse,
        stageGradeCounts: stageGradeCounts || undefined,
      };

      try {
        const { addIntEmbs: newEmbryos } = await addIntEmbryos({
          input: tempEmbryoInput,
          type: 1,
        });
        if (newEmbryos.length === 0) {
          dispatch(
            addToast({
              severity: 'error',
              summary: 'Server Error',
              detail: 'No embryos added',
              life: 3000,
            }),
          );
        }
        if (newEmbryos.length > 0) {
          params.set('temp', 'true');
          setParams(params);
          dispatch(
            addToast({
              severity: 'success',
              summary: 'Success',
              detail: 'Embryos added',
              life: 3000,
            }),
          );
        }

        if (tempEmbryosGridRef.current?.api) {
          tempEmbryosGridRef.current.api.setGridOption('suppressRowDrag', true);
        }
        if (fertEmbryosData) {
          const fertIndex = fertEmbryosData.findIndex(
            (fert) => fert.fertId === rowData?.fertId,
          );
          if (fertIndex !== -1) {
            const { liveEmbryoIds } = SelectionService.getSelection();
            const updatedEmbryos = [...newEmbryos];
            fertEmbryosData[fertIndex] = cloneDeep({
              ...fertEmbryosData[fertIndex],
              tempEmbryos: updatedEmbryos,
            });

            setTimeout(() => {
              if (tempEmbryosGridRef.current?.api) {
                const api = tempEmbryosGridRef.current.api;
                api.setGridOption('rowData', cloneDeep(updatedEmbryos));
                api.setGridOption('suppressRowDrag', false);
              }
              if (liveEmbryosGridRef.current?.api) {
                const api = liveEmbryosGridRef.current.api;
                SelectionService.updateSelection('live', [...liveEmbryoIds]);
                api.forEachNode((node) => {
                  const isSelected = liveEmbryoIds.includes(node.data.id);
                  node.setSelected(isSelected);
                });
              }
            }, 10);
          }
        }
      } finally {
        setTempEmbryoLoading(false);
        setIsUpdateRunning(false);

        gridApi.hideOverlay();
      }
    };

    const handleTempEmbryoSelectionChanged = useCallback((): void => {
      const selectedRows =
        tempEmbryosGridRef.current?.api.getSelectedRows() || [];
      const selectedIds = selectedRows.map((row) => row.embryoFreezeEntryId);

      const currentSelection = SelectionService.getSelection();

      const currentFertEmbryos =
        fertEmbryosData?.find((fert) => fert.fertId === rowData?.fertId)
          ?.tempEmbryos || [];

      const otherSelections = currentSelection.tempEmbryoIds.filter((id) => {
        const embryo = currentFertEmbryos.find(
          (e) => e.embryoFreezeEntryId === id,
        );
        return !embryo;
      });

      SelectionService.updateSelection('temp', [
        ...otherSelections,
        ...selectedIds,
      ]);

      onTempEmbryoSelectionChanged(rowData?.fertId as string);
    }, [rowData?.fertId, fertEmbryosData, onTempEmbryoSelectionChanged]);

    const handleLiveEmbryoSelectionChanged = useCallback((): void => {
      const selectedRows =
        liveEmbryosGridRef.current?.api.getSelectedRows() || [];
      const selectedIds = selectedRows.map((row) => row.id);

      const currentSelection = SelectionService.getSelection();

      const currentFertEmbryos =
        fertEmbryosData?.find((fert) => fert.fertId === rowData?.fertId)
          ?.liveEmbryos || [];

      const otherSelections = currentSelection.liveEmbryoIds.filter((id) => {
        const embryo = currentFertEmbryos.find((e) => e.id === id);
        return !embryo;
      });

      SelectionService.updateSelection('live', [
        ...otherSelections,
        ...selectedIds,
      ]);

      onLiveEmbryoSelectionChanged(rowData?.fertId as string);
    }, [rowData?.fertId, fertEmbryosData, onLiveEmbryoSelectionChanged]);

    return (
      <div className={`flex w-full flex-col gap-2 p-4`}>
        {!isSlaughterHouse && (
          <MemoizedTempEmbryoGrid
            tempEmbryos={
              fertEmbryosData?.find((fert) => fert.fertId === rowData?.fertId)
                ?.tempEmbryos as TOA_GetFertilizationsIntEmbryos[]
            }
            gridRef={tempEmbryosGridRef}
            rowData={rowData as TOA_GetFertilizationsForFreezing}
            onEdit={onEdit}
            handleUpdateTempEmbryo={handleUpdateTempEmbryo}
            handleTempEmbryoSelectionChanged={handleTempEmbryoSelectionChanged}
            handleAddTempEmbryos={handleAddTempEmbryos}
            handleDeactivateTempEmbryo={handleDeactivateTempEmbryo}
            refetchEmbryosFreezeData={refetchFertEmbryoData}
            allTempEmbryosSelected={allTempEmbryosSelected}
            isExport={isExport}
            mode={mode}
            tempEmbryoLoading={tempEmbryoLoading}
            isUpdateRunning={isUpdateRunning}
            node={node}
            api={api}
            isExpanded={isGridsExpanded}
          />
        )}
        <MemoizedLiveEmbryoGrid
          liveEmbryos={
            fertEmbryosData?.find((fert) => fert.fertId === rowData?.fertId)
              ?.liveEmbryos as TOA_GetFertilizationsEmbryos[]
          }
          tempEmbryos={
            fertEmbryosData?.find((fert) => fert.fertId === rowData?.fertId)
              ?.tempEmbryos as TOA_GetFertilizationsIntEmbryos[]
          }
          gridRef={liveEmbryosGridRef}
          onEdit={onEdit}
          handleUpdateEmbryo={handleUpdateEmbryo}
          handleLiveEmbryoSelectionChanged={handleLiveEmbryoSelectionChanged}
          isSlaughterHouse={false}
          mode={mode}
          isExport={isExport}
          isUpdateRunning={isUpdateRunning}
          node={node}
          api={api}
          isExpanded={isGridsExpanded}
          rowData={rowData as TOA_GetFertilizationsForFreezing}
        />
        <Dialog
          visible={showSubmitErrorDialog}
          onHide={() => setShowSubmitErrorDialog(false)}
          header="Error"
          className="error-dialog"
        >
          <div className="flex items-center gap-2">
            <i className="pi pi-exclamation-triangle text-red-500" />
            <p>Freeze Day and Grader must be selected</p>
          </div>
        </Dialog>
      </div>
    );
  },
);

export default memo(FreezeEmbryoGrid);
